/**
 * ServiceStepsView — pure presentational component.
 *
 * Renders an animated vertical timeline of resolved service steps.
 * Receives a flat list of `ResolvedServiceStep` objects (title, desc, status)
 * and has zero knowledge of workflow states, API calls, or service types.
 *
 * Usage:
 *   <ServiceStepsView steps={resolvedSteps} />
 */
import type { ResolvedServiceStep } from '../constants';
import { PALETTE_FOREST } from '../tokens';
import { StepNode } from './step-node';
import { StatusBadge } from './status-badge';

interface ServiceStepsViewProps {
  steps: ResolvedServiceStep[];
}

export function ServiceStepsView({ steps }: ServiceStepsViewProps) {
  if (steps.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        No steps available for this service.
      </p>
    );
  }

  return (
    <div className="py-2">
      {steps.map((step, idx) => {
        const isLast = idx === steps.length - 1;

        return (
          <div key={step.title} className="flex gap-4 relative">
            {/* Timeline connector — skip for last item */}
            {!isLast && (
              <div
                className="absolute left-[15px] top-8 w-0.5 transition-colors duration-300"
                style={{
                  bottom:          '-4px',
                  backgroundColor: step.status === 'completed'
                    ? PALETTE_FOREST
                    : 'hsl(var(--border))',
                }}
              />
            )}

            {/* Step node */}
            <div className="mt-0.5">
              <StepNode status={step.status} />
            </div>

            {/* Content */}
            <div className={`flex-1 ${isLast ? 'pb-2' : 'pb-8'}`}>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h4
                  className="text-sm font-semibold leading-snug"
                  style={{
                    color:   step.status === 'upcoming'
                      ? 'hsl(var(--muted-foreground))'
                      : 'hsl(var(--foreground))',
                    opacity: step.status === 'upcoming' ? 0.55 : 1,
                  }}
                >
                  {step.title}
                </h4>
                <StatusBadge status={step.status} />
              </div>

              <p
                className="text-xs leading-relaxed"
                style={{
                  color:   'hsl(var(--muted-foreground))',
                  opacity: step.status === 'upcoming' ? 0.5 : 1,
                }}
              >
                {step.desc}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
